import type {ReactNode} from 'react';

// Muestra el texto de la guia de uso (un Markdown sencillo escrito por
// nosotros): titulos #, ## y ###, parrafos, **negrita**, listas con "- " o
// "1. " y tablas con "|". Arma elementos de React (nunca inserta HTML).
type Bloque =
  | {tipo: 'titulo'; nivel: 1 | 2 | 3; texto: string}
  | {tipo: 'parrafo'; texto: string}
  | {tipo: 'lista'; ordenada: boolean; items: string[]}
  | {tipo: 'tabla'; encabezado: string[]; filas: string[][]};

const celdas = (linea: string) =>
  linea
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map(c => c.trim());

function bloques(texto: string): Bloque[] {
  const lineas = texto.replace(/\r/g, '').split('\n');
  const salida: Bloque[] = [];
  let i = 0;
  while (i < lineas.length) {
    const linea = lineas[i]!;
    if (!linea.trim()) {
      i++;
      continue;
    }
    const titulo = /^(#{1,3})\s+(.*)$/.exec(linea);
    if (titulo) {
      salida.push({tipo: 'titulo', nivel: titulo[1]!.length as 1 | 2 | 3, texto: titulo[2]!});
      i++;
      continue;
    }
    if (linea.trim().startsWith('|')) {
      const grupo: string[] = [];
      while (i < lineas.length && lineas[i]!.trim().startsWith('|')) grupo.push(lineas[i++]!);
      const [encabezado = '', , ...resto] = grupo;
      salida.push({tipo: 'tabla', encabezado: celdas(encabezado), filas: resto.map(celdas)});
      continue;
    }
    const ordenada = /^\d+\.\s/.test(linea);
    if (ordenada || /^-\s/.test(linea)) {
      const items: string[] = [];
      const patron = ordenada ? /^\d+\.\s+/ : /^-\s+/;
      while (i < lineas.length && patron.test(lineas[i]!)) items.push(lineas[i++]!.replace(patron, ''));
      salida.push({tipo: 'lista', ordenada, items});
      continue;
    }
    const parrafo: string[] = [];
    while (i < lineas.length && lineas[i]!.trim() && !/^(#|\||-\s|\d+\.\s)/.test(lineas[i]!))
      parrafo.push(lineas[i++]!);
    salida.push({tipo: 'parrafo', texto: parrafo.join(' ')});
  }
  return salida;
}

// **negrita** dentro de una linea.
function enLinea(texto: string): ReactNode[] {
  return texto.split(/(\*\*[^*]+\*\*)/g).map((parte, i) =>
    parte.startsWith('**') && parte.endsWith('**') ? (
      <strong key={i} className="font-semibold text-tinta">
        {parte.slice(2, -2)}
      </strong>
    ) : (
      parte
    ),
  );
}

export function TextoConFormato({texto}: {texto: string}) {
  return (
    <div className="space-y-3 text-sm leading-relaxed text-tinta">
      {bloques(texto).map((b, i) => {
        if (b.tipo === 'titulo')
          return b.nivel === 1 ? (
            <h2 key={i} className="text-xl font-semibold text-verde-oscuro">
              {enLinea(b.texto)}
            </h2>
          ) : b.nivel === 2 ? (
            <h3 key={i} className="pt-2 text-base font-semibold text-verde-oscuro">
              {enLinea(b.texto)}
            </h3>
          ) : (
            <h4 key={i} className="pt-1 font-semibold">
              {enLinea(b.texto)}
            </h4>
          );
        if (b.tipo === 'lista') {
          const Lista = b.ordenada ? 'ol' : 'ul';
          return (
            <Lista key={i} className={`space-y-1 pl-5 ${b.ordenada ? 'list-decimal' : 'list-disc'}`}>
              {b.items.map((item, j) => (
                <li key={j}>{enLinea(item)}</li>
              ))}
            </Lista>
          );
        }
        if (b.tipo === 'tabla')
          return (
            <div key={i} className="overflow-x-auto rounded-md border border-linea">
              <table className="w-full text-sm">
                <thead className="bg-papel">
                  <tr>
                    {b.encabezado.map((c, j) => (
                      <th key={j} className="px-3 py-2 text-left text-xs font-semibold text-apagado">
                        {enLinea(c)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.filas.map((fila, j) => (
                    <tr key={j} className="border-t border-linea/70 align-top">
                      {fila.map((c, k) => (
                        <td key={k} className="px-3 py-2">
                          {enLinea(c)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        return <p key={i}>{enLinea(b.texto)}</p>;
      })}
    </div>
  );
}
